/**
 * The studio header's standard look (the prototype's site header): Sign in with
 * Telegram for a guest; signed in, the Messages button with its count (the prototype's
 * .hdrMsg) and the client's initials and name opening a menu.
 * Draws the behavior half's state and calls its actions; holds no rule.
 */

import { partsOf, pieceRoot } from '../../contract/runtime';
import { onLink } from '../../ui/onLink';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import type { StudioHeaderRuntime } from './useStudioHeader';
import css from './StudioHeader.module.css';
import { contract } from './contract';

const part = partsOf(contract);

export function StudioHeaderView({ state, actions, options }: StudioHeaderRuntime) {
  return (
    <header className={css.header} {...pieceRoot(contract)} {...part('header')}>
      <div className={`${shared.wrap} ${css.bar}`}>
        <a className={css.mark} href="/" onClick={onLink(actions.goHome)} {...part('mark')}>
          {state.logoUrl && <img className={css.logo} src={state.logoUrl} alt="" {...part('logo')} />}
          <span className={`${css.name} ${state.logoUrl ? '' : css.wordmark}`} {...part('name')}>
            {state.name}
          </span>
        </a>
        {options.showNav && state.links.length > 0 && (
          <nav className={css.nav} aria-label="Site" {...part('nav')}>
            {state.links.map((l) => (
              <a
                key={l.id}
                className={css.link}
                href={l.href}
                aria-current={l.current ? (l.id === 'events' ? 'page' : 'true') : undefined}
                onClick={onLink(() => actions.open(l.id))}
                {...part('link', l.current && 'current')}
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}
        {options.showSignIn && state.session !== 'signedIn' && (
          <button
            type="button"
            className={css.signIn}
            aria-label="Sign in with Telegram"
            disabled={!state.signInReady || state.signIn === 'pending'}
            onClick={actions.signIn}
            {...part('signIn')}
          >
            <Icon name="tg" small />
            <span className={css.long}>Sign in with Telegram</span>
            <span className={css.short}>Sign in</span>
          </button>
        )}
        {state.client && (
          <button
            type="button"
            className={css.messages}
            aria-label={state.unread > 0 ? `Messages, ${state.unread} new` : 'Messages'}
            aria-expanded={state.messagesOpen}
            aria-controls="web-messages"
            onClick={actions.toggleMessages}
            {...part('messages')}
          >
            <Icon name="msg" />
            {state.unread > 0 && (
              <span className={css.count} aria-hidden="true" {...part('messagesBadge')}>
                {state.unread > 99 ? '99+' : state.unread}
              </span>
            )}
          </button>
        )}
        {state.client && (
          <div
            className={css.who}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) actions.toggleMenu(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') actions.toggleMenu(false);
            }}
          >
            <button
              type="button"
              className={css.user}
              aria-haspopup="menu"
              aria-expanded={state.menuOpen}
              onClick={() => actions.toggleMenu()}
              {...part('account', state.menuOpen && 'open')}
            >
              <span className={css.avatar} aria-hidden="true">
                {state.client.initials}
              </span>
              <span className={css.userName}>{state.client.name}</span>
              <Icon name="down" />
            </button>
            {state.menuOpen && (
              <div className={css.menu} role="menu" {...part('accountMenu')}>
                <a
                  role="menuitem"
                  className={css.menuItem}
                  href="/account/bookings"
                  onClick={onLink(() => actions.openAccount('bookings'))}
                  {...part('menuBookings')}
                >
                  Bookings
                </a>
                <button type="button" role="menuitem" className={css.menuItem} onClick={actions.toggleMessages} {...part('menuMessages')}>
                  Messages
                  {state.unread > 0 && <small className={css.menuNew}>{state.unread} new</small>}
                </button>
                <a
                  role="menuitem"
                  className={css.menuItem}
                  href="/account/profile"
                  onClick={onLink(() => actions.openAccount('profile'))}
                  {...part('menuProfile')}
                >
                  Profile
                </a>
                <div className={css.menuSep} role="separator" />
                <button type="button" role="menuitem" className={css.menuItem} onClick={actions.signOut} {...part('signOut')}>
                  Sign out
                </button>
                {state.signOutFailed && (
                  <p className={css.menuError} role="alert" {...part('signOutError')}>
                    Sign out did not go through. Please try again.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
